// Règles de progression : taille du labyrinthe, vitesse du chrono (verdict : voir core/chase.js)
import {
  MIN_CELL_PX, MAX_GRID_SIZE, MIN_GRID_SIZE,
  TIMER_BASE_MS, TIMER_STEP_MS, TIMER_MIN_MS, RAT_EARLY_SLOWDOWN, RAT_EARLY_LEVELS,
} from '../config/constants.js';

/** Taille max jouable pour une largeur CSS donnée (une case >= MIN_CELL_PX) */
export function getMaxGridSize(cssWidth) {
  const width = cssWidth > 0 ? cssWidth : 375;
  return Math.max(MIN_GRID_SIZE, Math.min(MAX_GRID_SIZE, Math.floor(width / MIN_CELL_PX)));
}

/**
 * Croissance linéaire de la taille, arrondie à la case (un pas de 1,5 donne 5, 7, 8, 10, 11, 13, 14, 16), plafonnée par ce que
 * l'écran peut afficher et par MAX_GRID_SIZE.
 */
export function getLevelSize(level, sizeStep, maxSize) {
  return Math.min(maxSize, MAX_GRID_SIZE, Math.round(MIN_GRID_SIZE + (level - 1) * sizeStep));
}

/** Intervalle (ms) du chrono de base, qui cadence aussi l'avance automatique de la souris au clic */
export function getTimerInterval(level, speedFactor = 1) {
  const base = Math.max(TIMER_MIN_MS, TIMER_BASE_MS - level * TIMER_STEP_MS);
  return base / speedFactor;
}

/**
 * Intervalle (ms) entre deux tics du rat (un pas, ou une bouchée de fromage) : le chrono de base,
 * ralenti sur les premiers niveaux (RAT_EARLY_SLOWDOWN au niveau 1, sans effet dès RAT_EARLY_LEVELS).
 */
export function getRatInterval(level, speedFactor = 1) {
  const remaining = Math.max(0, (RAT_EARLY_LEVELS - level) / (RAT_EARLY_LEVELS - 1));
  const slowdown = 1 + (RAT_EARLY_SLOWDOWN - 1) * remaining;
  return getTimerInterval(level, speedFactor) * slowdown;
}

/**
 * Suivi d'un geste trackpad à deux doigts, pour écarter l'inertie : après qu'on a levé les doigts,
 * macOS continue d'envoyer des événements wheel qui décroissent doucement. Un geste commence après
 * un silence de plus de `gapMs` ; dès que l'amplitude retombe sous `inertiaRatio` × le pic en
 * décroissant, la suite est de l'inertie — jusqu'à ce qu'elle remonte franchement (doigts reposés).
 * @param {{lastTime:number, peak:number, prev:number, coasting:boolean}|null} gesture état précédent
 * @param {number} magnitude amplitude de l'événement (px)
 * @param {number} time horodatage (ms)
 * @returns {{ gesture: object, isNew: boolean, inertia: boolean }}
 */
export function trackWheelGesture(gesture, magnitude, time, { gapMs, inertiaRatio }) {
  if (!gesture || time - gesture.lastTime > gapMs) {
    return { gesture: { lastTime: time, peak: magnitude, prev: magnitude, coasting: false }, isNew: true, inertia: false };
  }
  let { peak, coasting } = gesture;
  if (coasting && magnitude > gesture.prev * 1.5) {
    coasting = false; // nouvelle poussée des doigts pendant l'inertie
    peak = magnitude;
  } else if (!coasting && magnitude < peak * inertiaRatio && magnitude <= gesture.prev) {
    coasting = true;
  }
  if (!coasting) peak = Math.max(peak, magnitude);
  return { gesture: { lastTime: time, peak, prev: magnitude, coasting }, isNew: false, inertia: coasting };
}
